import React, { useCallback, useEffect, useState } from 'react';
import { Info, Rocket, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchReadyAppsCatalogApi } from '../../api/marketplaceApps.api';
import { Button } from '../../components/ui/Button';
import { PackagePricingLink } from '../projects/ProductCreateBanner';
import { DeployReadyAppModal } from './DeployReadyAppModal';
import { ReadyAppDetailsModal } from './ReadyAppDetailsModal';
import { canUseReadyApps, hasPublishSlotRemaining } from './readyAppQuota';
import { readyAppPackageLabelKey } from './readyAppDisplay';
import { useI18n } from '../../hooks/useI18n';
import { useMyPackage } from '../../hooks/useMyPackage';
import { classNames } from '../../utils/classNames';

export const ReadyAppsSection = () => {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const entitlement = useMyPackage();
  const [catalog, setCatalog] = useState({ items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailsApp, setDetailsApp] = useState(null);

  const packageLoaded = Boolean(entitlement);
  const allowedByPackage = !packageLoaded || canUseReadyApps(entitlement);
  const hasSlot = !packageLoaded || hasPublishSlotRemaining(entitlement);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const nextCatalog = await fetchReadyAppsCatalogApi();
      setCatalog(nextCatalog);
    } catch (loadError) {
      setError(loadError?.message || t('readyAppsLoadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-5">
      <h2 className="text-base font-semibold">{t('readyAppsTitle')}</h2>

      {packageLoaded && !allowedByPackage ? (
        <div className="overflow-hidden rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-start dark:border-amber-500/30 dark:bg-amber-500/10">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">
              <Sparkles size={18} strokeWidth={2.2} />
            </span>
            <div className="min-w-0 flex-1 text-sm leading-6 text-amber-950 dark:text-amber-100">
              <p className="font-bold">{t('readyAppsNeedsPackage')}</p>
              <p className="mt-1 text-amber-900/80 dark:text-amber-100/80">
                {t('readyAppsNeedsPackageHint')}{' '}
                <PackagePricingLink>{t('pricingPurchaseTitle')}</PackagePricingLink>
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200">
          <p className="flex-1">{error}</p>
          <Button type="button" variant="secondary" className="px-3 py-1.5" onClick={load}>
            {t('readyAppsRetry')}
          </Button>
        </div>
      ) : null}

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {[0, 1, 2, 3].map((item) => (
            <div
              key={item}
              className="h-64 animate-pulse rounded-2xl border border-neutral-200 bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-900"
            />
          ))}
        </div>
      ) : catalog.items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-200 bg-white px-6 py-10 text-center dark:border-neutral-800 dark:bg-neutral-950">
          <p className="text-sm text-neutral-500">{t('readyAppsCatalogEmpty')}</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {catalog.items.map((app) => {
            const canDeploy = Boolean(app.allowed) && hasSlot;
            const planLabel = t(readyAppPackageLabelKey(app.requiredPackageSlug));
            return (
              <article
                key={app.key}
                className="flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition hover:border-neutral-300 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:border-neutral-700"
              >
                <div className="relative h-36 bg-neutral-950">
                  <img src={app.screenshotUrl} alt="" className="h-full w-full object-cover" />
                  <span
                    className={classNames(
                      'absolute end-3 top-3 rounded-full px-2.5 py-1 text-xs font-semibold',
                      app.requiredPackageSlug === 'promax'
                        ? 'bg-violet-600 text-white'
                        : 'bg-sky-600 text-white'
                    )}
                  >
                    {planLabel}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center gap-3">
                    <img src={app.iconUrl} alt="" className="h-10 w-10 rounded-2xl" />
                    <h3 className="text-base font-semibold text-neutral-950 dark:text-white">
                      {language === 'fa' ? app.titleFa : app.titleEn}
                    </h3>
                  </div>
                  <p className="mt-2 flex-1 text-sm leading-6 text-neutral-500">
                    {language === 'fa' ? app.descriptionFa : app.descriptionEn}
                  </p>
                  {!app.allowed ? (
                    <p className="mt-3 text-xs text-amber-700 dark:text-amber-200">
                      {t('readyAppsLockedHint', { plan: planLabel })}
                    </p>
                  ) : null}
                  <div className="mt-5 flex flex-wrap gap-2">
                    <Button type="button" variant="secondary" onClick={() => setDetailsApp(app)}>
                      <Info size={16} />
                      {t('readyAppsDetails')}
                    </Button>
                    <Button type="button" disabled={!canDeploy} onClick={() => setSelectedApp(app)}>
                      <Rocket size={16} />
                      {t('readyAppsDeploy')}
                    </Button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <ReadyAppDetailsModal
        open={Boolean(detailsApp)}
        app={detailsApp}
        canDeploy={Boolean(detailsApp?.allowed) && hasSlot}
        onClose={() => setDetailsApp(null)}
        onDeploy={() => {
          setSelectedApp(detailsApp);
          setDetailsApp(null);
        }}
      />

      <DeployReadyAppModal
        open={Boolean(selectedApp)}
        app={selectedApp}
        onClose={() => setSelectedApp(null)}
        onCreated={(created) => {
          navigate(`/apps/${created.id}`);
        }}
      />
    </div>
  );
};
